import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  getDocs,
  Timestamp,
  DocumentData,
  QueryConstraint,
} from 'firebase/firestore';

import { db } from './config';
import type { UserProfile } from '@shared/types/user';

/**
 * Convert Firestore timestamp to Date
 */
function timestampToDate(timestamp: Timestamp | Date | null | undefined): Date {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  return timestamp.toDate();
}

/**
 * Convert Date to Firestore timestamp
 */
function dateToTimestamp(date: Date): Timestamp {
  return Timestamp.fromDate(date);
}

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(userId: string): Promise<UserProfile> {
  const userDoc = await getDoc(doc(db, 'users', userId));

  if (!userDoc.exists()) {
    throw new Error(`User profile not found: ${userId}`);
  }

  const data = userDoc.data();

  return {
    id: userDoc.id,
    email: data.email,
    displayName: data.displayName || null,
    phoneNumber: data.phoneNumber || null,
    role: data.role,
    emailVerified: data.emailVerified || false,
    phoneVerified: data.phoneVerified || false,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
    photoURL: data.photoURL || null,
    locationId: data.locationId || null,
    preferences: data.preferences || undefined,
  };
}

/**
 * Create user profile in Firestore
 */
export async function createUserProfile(profile: UserProfile): Promise<void> {
  const userRef = doc(db, 'users', profile.id);

  await setDoc(userRef, {
    email: profile.email,
    displayName: profile.displayName,
    phoneNumber: profile.phoneNumber,
    role: profile.role,
    emailVerified: profile.emailVerified,
    phoneVerified: profile.phoneVerified,
    createdAt: dateToTimestamp(profile.createdAt),
    updatedAt: dateToTimestamp(profile.updatedAt),
    photoURL: profile.photoURL,
    locationId: profile.locationId || null,
    preferences: profile.preferences || null,
  });
}

/**
 * Update user profile in Firestore
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<UserProfile>
): Promise<void> {
  const userRef = doc(db, 'users', userId);
  const firestoreUpdates: DocumentData = {
    updatedAt: dateToTimestamp(new Date()),
  };

  // Only include fields that are being updated
  if (updates.displayName !== undefined) {
    firestoreUpdates.displayName = updates.displayName;
  }
  if (updates.phoneNumber !== undefined) {
    firestoreUpdates.phoneNumber = updates.phoneNumber;
    firestoreUpdates.phoneVerified = !!updates.phoneNumber;
  }
  if (updates.role !== undefined) {
    firestoreUpdates.role = updates.role;
  }
  if (updates.photoURL !== undefined) {
    firestoreUpdates.photoURL = updates.photoURL;
  }
  if (updates.locationId !== undefined) {
    firestoreUpdates.locationId = updates.locationId;
  }
  if (updates.preferences !== undefined) {
    firestoreUpdates.preferences = updates.preferences;
  }

  await updateDoc(userRef, firestoreUpdates);
}

/**
 * Generic Firestore helper: Get document by ID
 */
export async function getDocument<T>(
  collectionName: string,
  documentId: string
): Promise<T | null> {
  const docRef = doc(db, collectionName, documentId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return { id: docSnap.id, ...docSnap.data() } as T;
}

/**
 * Generic Firestore helper: Query documents
 */
export async function queryDocuments<T>(
  collectionName: string,
  ...constraints: QueryConstraint[]
): Promise<T[]> {
  const q = query(collection(db, collectionName), ...constraints);
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as T[];
}

/**
 * Generic Firestore helper: Create or update document
 */
export async function setDocument<T extends { id: string }>(
  collectionName: string,
  data: T
): Promise<void> {
  const { id, ...docData } = data;
  const docRef = doc(db, collectionName, id);

  // Convert Date objects to Timestamps
  const processedData = processDatesForFirestore(docData);

  await setDoc(docRef, {
    ...processedData,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Helper to convert Date objects to Firestore Timestamps recursively
 */
function processDatesForFirestore(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (obj instanceof Date) {
    return Timestamp.fromDate(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(processDatesForFirestore);
  }

  if (typeof obj === 'object') {
    const processed: any = {};
    for (const key in obj) {
      processed[key] = processDatesForFirestore(obj[key]);
    }
    return processed;
  }

  return obj;
}

